/**
 * SessionSplash — the one-shot "instrument booting" screen shown while the app
 * restores a session on hard reload (mints a fresh in-memory access token from
 * the httpOnly refresh cookie before routing). Rebranded to the cockpit
 * identity: the canvas (--bg), a faint hairline grid, and the S-leaf brand
 * mark (the real logo asset, 44px), with a subtle transform/opacity pulse
 * (§1.7 — reduced-motion is honoured globally via index.css). The
 * canvas/grid stay theme-aware tokens; the mark itself is the brand-constant
 * corporate artwork (the sanctioned token-purity exception for logo colors —
 * see BrandMark's doc comment). No data: URIs, no gradients, no legacy
 * blue/cyan. Visible for well under a second.
 */
import { BrandMark } from '@/components/auth/AuthBrandPanel';

export function SessionSplash() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Restoring your session"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        overflow: 'hidden',
        isolation: 'isolate',
        animation: 'fadeIn var(--dur) var(--ease-out)',
      }}
    >
      {/* Hairline engineering grid, faded toward the edges (CSS-only; the
          opaque mask stop reuses a token — masks only read alpha). */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '38px 38px',
          maskImage:
            'radial-gradient(ellipse 80% 70% at 50% 50%, var(--seedz-ink) 35%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 70% at 50% 50%, var(--seedz-ink) 35%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* S-leaf brand mark — subtle breathe pulse (transform/opacity only) */}
      <div
        aria-hidden="true"
        style={{
          position: 'relative',
          zIndex: 1,
          animation: 'splashPulse 1.6s var(--ease-in-out) infinite alternate',
        }}
      >
        <BrandMark glyphSize={44} wordmark={false} />
      </div>

      <span
        style={{
          position: 'relative',
          zIndex: 1,
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-2xs)',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--text-subtle)',
        }}
      >
        Restoring session
      </span>

      <style>{`
        @keyframes splashPulse {
          0%   { opacity: 0.72; transform: scale(1); }
          100% { opacity: 1;    transform: scale(1.04); }
        }
      `}</style>
    </div>
  );
}
